import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetCariReportDetailQuery, useGetCariReportDetailCustomersQuery } from '../../../../api/endpoints/accountingReport';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { InputText } from 'primereact/inputtext';
import { formatDateForApi } from '../../../../utils/dateUtils';

const getFirstDayOfMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
};

const getLastDayOfMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

const useCariDetailReportData = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const [rows, setRows] = useState([]);

    const [filters, setFilters] = useState({
        startDate: getFirstDayOfMonth(),
        endDate: getLastDayOfMonth(),
        typeOfMovement: null
    });

    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
    const [customerSearchText, setCustomerSearchText] = useState('');
    const [dropdownFilterValue, setDropdownFilterValue] = useState('');
    const dropdownDebounceRef = useRef(null);

    const prevOptionsRef = useRef([]);
    const { data: customerData, isFetching: fetchingCustomers } =
        useGetCariReportDetailCustomersQuery({
            SearchText: customerSearchText,
            RowCount: 50
        });

    const customerOptions = useMemo(() => {
        if (!customerData) return prevOptionsRef.current;
        const mapped = customerData.map(c => ({
            label: String(c.label || c.name || c.fullName || ''),
            value: c.value != null ? Number(c.value) : null
        })).filter(opt => opt.value != null);
        if (mapped.length > 0) {
            prevOptionsRef.current = mapped;
        }
        return mapped;
    }, [customerData]);

    const currentOptions = useMemo(() => {
        const opts = customerOptions || [];
        if (
            selectedCustomerId != null &&
            selectedCustomerOption &&
            !opts.some((opt) => Number(opt.value) === Number(selectedCustomerOption.value))
        ) {
            return [selectedCustomerOption, ...opts];
        }
        return opts;
    }, [customerOptions, selectedCustomerId, selectedCustomerOption]);

    const handleDropdownFilterInputChange = useCallback((val) => {
        setDropdownFilterValue(val);
        if (dropdownDebounceRef.current) clearTimeout(dropdownDebounceRef.current);
        dropdownDebounceRef.current = setTimeout(() => {
            setCustomerSearchText((val ?? '').trim());
        }, 400);
    }, []);

    useEffect(() => {
        return () => {
            if (dropdownDebounceRef.current) clearTimeout(dropdownDebounceRef.current);
        };
    }, []);

    const dropdownFilterTemplate = useCallback(
        () => (
            <div
                className="p-dropdown-filter-container"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <InputText
                    value={dropdownFilterValue}
                    onChange={(e) => handleDropdownFilterInputChange(e.target.value)}
                    className="p-dropdown-filter w-full"
                    placeholder={th('common.search') || 'Ara'}
                    autoFocus
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
                <span className="p-dropdown-filter-icon pi pi-search" />
            </div>
        ),
        [dropdownFilterValue, handleDropdownFilterInputChange, th]
    );

    const handleDropdownChange = useCallback(
        (value) => {
            setDropdownFilterValue('');
            setCustomerSearchText('');

            if (value) {
                const found = customerOptions.find(
                    (opt) => Number(opt.value) === Number(value)
                );
                setSelectedCustomerOption(
                    found || { label: `${value}`, value: Number(value) }
                );
                setSelectedCustomerId(value);
            } else {
                setSelectedCustomerId(null);
                setSelectedCustomerOption(null);
            }
        },
        [customerOptions]
    );

    useEffect(() => {
        if (selectedCustomerId == null) return;
        const found = customerOptions.find(
            (opt) => Number(opt.value) === Number(selectedCustomerId)
        );
        if (found) setSelectedCustomerOption(found);
    }, [customerOptions, selectedCustomerId]);

    const customerDropdown = useMemo(
        () => ({
            key: 'customer',
            value: selectedCustomerId,
            options: currentOptions,
            placeholder: th('preAccounting.selectCustomer'),
            loading: fetchingCustomers,
            disabled: false,
            filterTemplate: dropdownFilterTemplate,
            onShow: () => {},
            onChange: handleDropdownChange,
            emptyMessage: th('common.noRecords')
        }),
        [
            selectedCustomerId,
            currentOptions,
            th,
            fetchingCustomers,
            dropdownFilterTemplate,
            handleDropdownChange
        ]
    );

    const queryParams = useMemo(() => ({
        StartDate: formatDateForApi(filters.startDate) || null,
        EndDate: formatDateForApi(filters.endDate) || null,
        TypeOfMovement: filters.typeOfMovement ?? undefined,
        ...(selectedCustomerId != null && { CustomerId: selectedCustomerId })
    }), [filters, selectedCustomerId]);

    const { isLoading, isFetching, currentData } = useGetCariReportDetailQuery(queryParams, {
        refetchOnMountOrArgChange: true
    });

    useEffect(() => {
        if (currentData) {
            setRows(Array.isArray(currentData.data) ? currentData.data : []);
        } else if (isFetching) {
            setRows([]);
        }
    }, [currentData, isFetching]);

    const loading = isLoading || isFetching;

    const handleResetFilters = useCallback(() => {
        setRows([]);
        setFilters({
            startDate: getFirstDayOfMonth(),
            endDate: getLastDayOfMonth(),
            typeOfMovement: null
        });
        setSelectedCustomerId(null);
        setSelectedCustomerOption(null);
        setDropdownFilterValue('');
        setCustomerSearchText('');
    }, []);

    const handleApplyFilters = useCallback((newFilters) => {
        setRows([]);
        setFilters({
            startDate: newFilters.startDate,
            endDate: newFilters.endDate,
            typeOfMovement: newFilters.typeOfMovement ?? null
        });
    }, []);

    const exportParams = useMemo(() => ({
        StartDate: formatDateForApi(filters.startDate) || null,
        EndDate: formatDateForApi(filters.endDate) || null,
        TypeOfMovement: filters.typeOfMovement ?? undefined,
        ...(selectedCustomerId != null && { CustomerId: selectedCustomerId }),
        LanguageId: getCurrentLanguageId(),
        CompressionType: 0
    }), [filters, selectedCustomerId, getCurrentLanguageId]);

    return {
        rows,
        loading,
        filters,
        handleResetFilters,
        handleApplyFilters,
        exportParams,
        customerDropdown
    };
};

export default useCariDetailReportData;
